import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Theme,
  Tooltip,
  Typography,
} from "@mui/material";
import { type ChangeEvent, type Dispatch, type MouseEvent, useState } from "react";

import { ActivitySortField, GetActivitiesQuery } from "@/graphql/graphql";
import {
  formatUtcCalendarDateOrdinalLong,
  transformStringToBrowserTimeZoneAndFullTime,
} from "@/utils/date-utils";
import { formatEnumLabel } from "@/utils/format";

import ActivityDialog from "../ActivityDialog/ActivityDialog";
import { planTableToggleAriaLabel, sortOrderToMui } from "../CreateCallPlanModal.utils";
import {
  ActivitiesQueryAction,
  ActivitiesQueryState,
} from "./ActivitiesTable.activitiesQueryState";
import {
  activitiesTableLoadingOverlaySx,
  activitiesTableRowClickableSx,
  addActivityBodyCellSx,
  addActivityHeaderCellSx,
  futureProgramWeekHintPillSx,
  futureProgramWeekHintWrapperSx,
  lastDeliveredCellClusterSx,
  neutralPillFilterMatchSx,
  neutralPillSx,
  nextCallPlanTooltipContentSx,
  nextCallPlanTooltipHeadingSx,
  nextCallPlanTooltipWeekSx,
  paginationContainerSx,
  pillClusterBodyCellSx,
  pillClusterHeaderCellSx,
  pillContainerSx,
  planAddButtonBlockedSx,
  planAddButtonEnabledSx,
  planRemoveButtonSx,
  planToggleGlyphSx,
  tableBodyCellSx,
  tableContainerSx,
  tableHeaderCellSx,
  tablePaginationSx,
  tableScrollAreaSx,
  tableWrapperSx,
} from "./ActivitiesTable.styles";

type ActivityRow = GetActivitiesQuery["activities"]["activities"][number];
type EarliestFutureCallPlan = NonNullable<ActivityRow["earliestFutureCallPlan"]>;

const CallPlanUsageTooltip: React.FC<{ callPlan: EarliestFutureCallPlan }> = ({
  callPlan: { programWeek, startDate, endDate },
}) => {
  const start = formatUtcCalendarDateOrdinalLong(startDate?.toString());
  const end = endDate ? formatUtcCalendarDateOrdinalLong(endDate.toString()) : "Active";
  const dateRange = start ? `${start} - ${end}` : "-";

  return (
    <Tooltip
      title={
        <Box sx={nextCallPlanTooltipContentSx}>
          <Box component="div" sx={nextCallPlanTooltipHeadingSx}>
            Next Call Plan usage:
          </Box>
          <Box component="div" sx={nextCallPlanTooltipWeekSx}>
            Program week {programWeek}
          </Box>
          <Box component="div">{dateRange}</Box>
        </Box>
      }
    >
      <Box component="span" sx={futureProgramWeekHintWrapperSx}>
        <Box component="span" sx={futureProgramWeekHintPillSx}>
          +
        </Box>
      </Box>
    </Tooltip>
  );
};

interface ActivitiesTableProps {
  /** When true, hides call-plan add/remove and last delivered columns (e.g. admin activity library page). */
  libraryMode?: boolean;
  activitiesLoading: boolean;
  activities: ActivityRow[];
  totalActivities: number;
  queryState: ActivitiesQueryState;
  dispatchQuery: Dispatch<ActivitiesQueryAction>;
  typeKeysSelected: Record<string, boolean>;
  onPageChange: (_: MouseEvent<HTMLButtonElement> | null, newPage: number) => void;
  onRowsPerPageChange: (event: ChangeEvent<HTMLInputElement>) => void;
  activityIdsSelected: Record<number, boolean>;
  atCapacity: boolean;
  onAddActivity?: (activity: ActivityRow) => void;
  onRemoveActivity?: (activityId: number) => void;
}

interface ActivityTableColumnSortLabelProps {
  column: ActivitySortField;
  queryState: ActivitiesQueryState;
  dispatchQuery: Dispatch<ActivitiesQueryAction>;
}

const ActivityTableColumnSortLabel: React.FC<ActivityTableColumnSortLabelProps> = ({
  column,
  queryState,
  dispatchQuery,
}) => (
  <TableSortLabel
    active={queryState.sortField === column}
    direction={sortOrderToMui(queryState.sortDirection)}
    onClick={() => dispatchQuery({ type: "TOGGLE_SORT", field: column })}
  >
    {formatEnumLabel(column)}
  </TableSortLabel>
);

const ActivitiesTable: React.FC<ActivitiesTableProps> = ({
  libraryMode = false,
  activitiesLoading,
  activities,
  totalActivities,
  queryState,
  dispatchQuery,
  typeKeysSelected,
  onPageChange,
  onRowsPerPageChange,
  activityIdsSelected,
  atCapacity,
  onAddActivity,
  onRemoveActivity,
}) => {
  const [detailActivityId, setDetailActivityId] = useState<number | null>(null);

  return (
    <>
      <Box sx={tableWrapperSx}>
        <Box sx={tableScrollAreaSx}>
          <TableContainer sx={tableContainerSx}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {!libraryMode && (
                    <TableCell sx={addActivityHeaderCellSx} aria-label="Add to plan" />
                  )}
                  <TableCell sx={tableHeaderCellSx}>
                    <ActivityTableColumnSortLabel
                      column={ActivitySortField.Title}
                      queryState={queryState}
                      dispatchQuery={dispatchQuery}
                    />
                  </TableCell>
                  {!libraryMode && <TableCell sx={tableHeaderCellSx}>Last delivered</TableCell>}
                  <TableCell sx={pillClusterHeaderCellSx}>Activity Types</TableCell>
                  <TableCell sx={tableHeaderCellSx}>
                    <ActivityTableColumnSortLabel
                      column={ActivitySortField.Difficulty}
                      queryState={queryState}
                      dispatchQuery={dispatchQuery}
                    />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activities.map((activity) => {
                  const alreadyAdded = activityIdsSelected[activity.id];
                  const addBlocked = !alreadyAdded && atCapacity;

                  return (
                    <TableRow
                      key={activity.id}
                      hover
                      sx={activitiesTableRowClickableSx}
                      onClick={() => setDetailActivityId(activity.id)}
                    >
                      {!libraryMode && (
                        <TableCell sx={addActivityBodyCellSx}>
                          <IconButton
                            size="small"
                            aria-label={planTableToggleAriaLabel(
                              activity.title,
                              alreadyAdded ? "remove" : addBlocked ? "blocked" : "add"
                            )}
                            disabled={addBlocked}
                            onClick={(e) => {
                              e.stopPropagation();
                              alreadyAdded
                                ? onRemoveActivity?.(activity.id)
                                : onAddActivity?.(activity);
                            }}
                            sx={
                              alreadyAdded
                                ? planRemoveButtonSx
                                : addBlocked
                                ? planAddButtonBlockedSx
                                : planAddButtonEnabledSx
                            }
                          >
                            <Typography component="span" sx={planToggleGlyphSx}>
                              {alreadyAdded ? "-" : "+"}
                            </Typography>
                          </IconButton>
                        </TableCell>
                      )}
                      <TableCell sx={tableBodyCellSx}>{activity.title}</TableCell>
                      {!libraryMode && (
                        <TableCell sx={tableBodyCellSx}>
                          <Box sx={lastDeliveredCellClusterSx}>
                            {(activity.lastDeliveryTime || !activity.earliestFutureCallPlan) && (
                              <Typography component="span" variant="body2">
                                {activity.lastDeliveryTime
                                  ? transformStringToBrowserTimeZoneAndFullTime(
                                      activity.lastDeliveryTime
                                    )
                                  : "-"}
                              </Typography>
                            )}
                            {activity.earliestFutureCallPlan && (
                              <CallPlanUsageTooltip callPlan={activity.earliestFutureCallPlan} />
                            )}
                          </Box>
                        </TableCell>
                      )}
                      <TableCell sx={pillClusterBodyCellSx}>
                        {activity.activityTypes.length ? (
                          <Box sx={pillContainerSx}>
                            {activity.activityTypes.map((type) => (
                              <Chip
                                key={type.activityTypeKey}
                                size="small"
                                label={type.activityTypeLabel}
                                sx={
                                  {
                                    ...neutralPillSx,
                                    cursor: "pointer",
                                    ...(queryState.typeKeys?.length &&
                                    typeKeysSelected[type.activityTypeKey]
                                      ? neutralPillFilterMatchSx
                                      : {}),
                                  } as SxProps<Theme>
                                }
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2">-</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={tableBodyCellSx}>
                        {formatEnumLabel(activity.difficulty)}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!activities.length && !activitiesLoading && (
                  <TableRow>
                    <TableCell colSpan={libraryMode ? 3 : 5} align="center">
                      No activities found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            {activitiesLoading && (
              <Box sx={activitiesTableLoadingOverlaySx}>
                <CircularProgress />
              </Box>
            )}
          </TableContainer>
        </Box>

        <Box sx={paginationContainerSx}>
          <TablePagination
            component="div"
            count={totalActivities}
            page={queryState.page}
            onPageChange={onPageChange}
            rowsPerPage={queryState.pageSize}
            onRowsPerPageChange={onRowsPerPageChange}
            rowsPerPageOptions={[20, 50, 100]}
            showFirstButton
            showLastButton
            sx={tablePaginationSx}
          />
        </Box>
      </Box>

      <ActivityDialog
        activityId={detailActivityId}
        onClose={() => setDetailActivityId(null)}
        titleHint={activities.find((a) => a.id === detailActivityId)?.title}
      />
    </>
  );
};

export default ActivitiesTable;
